import { FC, HTMLAttributes } from 'react'
import { Severity } from '@/root/shared-types/ui/ui-types'
import cn from 'classnames'
import { sanitizeHtml } from '@/root/shared-helpers'
import { CrossIcon } from '@/icons/CrossIcon'
import { testIdHelpers } from '@/root/shared-consts'

type TextAlign = 'left' | 'center' | 'right'

const textAlignClass: Record<TextAlign, string> = {
  left: 'text-start',
  center: 'text-center',
  right: 'text-end',
}

// A close button needs an accessible name, so `closeLabel` comes together with `onClose`
type CloseProps = { onClose?: never; closeLabel?: never } | { onClose: () => void; closeLabel: string }

type BannerProps = HTMLAttributes<HTMLDivElement> &
  CloseProps & {
    variant: Severity
    align?: TextAlign
    body?: string
    bodyClassName?: string
    title?: string
  }

interface BannerConfig {
  iconColor: string
  styles: string
}

const variantConfig: Record<Severity, BannerConfig> = {
  success: { iconColor: 'success-dark', styles: 'border-success-dark bg-success-very-light text-success-dark' },
  warning: { iconColor: 'warning-dark', styles: 'border-warning-dark bg-warning-very-light text-warning-dark' },
  error: { iconColor: 'error', styles: 'border-error bg-error-very-light text-error' },
  info: { iconColor: 'base-dark', styles: 'border-base-dark bg-base-very-light text-base-dark' },
  neutral: { iconColor: 'neutral-dark', styles: 'border-neutral-dark bg-neutral-very-light text-neutral-dark' },
}

export const Banner: FC<BannerProps> = ({
  body,
  title,
  variant,
  align = 'center',
  children,
  className,
  bodyClassName,
  closeLabel,
  onClose,
  ...rest
}) => {
  const config = variantConfig[variant]

  return (
    <div
      data-testid={testIdHelpers.messageBox(variant)}
      {...rest}
      className={cn(
        'relative flex flex-col rounded-2xl border p-4 text-lg',
        textAlignClass[align],
        onClose && 'pe-10',
        config.styles,
        className
      )}
    >
      {onClose && (
        <button
          type="button"
          aria-label={closeLabel}
          className="absolute inset-e-4 top-4 -m-1.5 cursor-pointer p-1.5"
          onClick={onClose}
        >
          <CrossIcon width={15} height={15} color={config.iconColor} />
        </button>
      )}
      {title && <h2 className="text-2xl">{title}</h2>}
      {body && <p className={bodyClassName} dangerouslySetInnerHTML={{ __html: sanitizeHtml(body) }}></p>}
      {children}
    </div>
  )
}
